import { getSnapshot, types } from 'mobx-state-tree';
// import { JsonType } from '@mcn-platform/models/src/Schema';
// import { Course } from '@mcn-platform/models/src/CourseModel';
// import { Document } from '@mcn-platform/models/src/DocumentModel';
// import { Microcast } from '@mcn-platform/models/src/MicrocastModel';
// import { Program } from '@mcn-platform/models/src/ProgramModel';
// import { ToolInvocation } from 'ai';
import { omit } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { getChatMessageTextForSync } from '../clientAi/getChatMessageTextForSync';
export const JsonType = types.union(types.string, types.number, types.integer, types.Date, types.boolean, types.map(types.late(() => JsonType)), types.array(types.late(() => JsonType)), types.undefined, types.null);
const ItemReference = types.model({
    key: types.identifier,
    type: types.string,
    // target: types.maybe(types.reference(
    //   types.union(Course, Document, Microcast, Program),
    // )),
    title: types.maybe(types.string),
    image: types.maybe(types.string),
    description: types.maybe(types.string),
    url: types.maybe(types.string),
    /** Catalog entity id (course, document, …); used when url is missing. */
    id: types.maybe(types.string),
    organizationID: types.maybe(types.string),
    itemID: types.maybe(types.string),
    slug: types.maybe(types.string),
    price: types.maybe(types.number),
    variants: types.maybe(types.frozen(JsonType)),
    showAddToCart: types.maybe(types.boolean),
    customizable: types.maybe(types.boolean),
    /**
     * Shopify /products/{handle}.js–shaped payload pre-loaded by the backend when
     * `mergeProductIntegrationData` is enabled. When present, product cards use this
     * directly instead of calling `commerce.getProductData()` on the client.
     */
    productPayload: types.maybe(types.frozen(JsonType)),
    // lessonType: types.maybe(types.string),
    // lessonID: types.maybe(types.string),
});
const ConversationMessage = types.model({
    id: types.identifier,
    content: types.string,
    role: types.string,
    createdAt: types.maybeNull(types.Date),
    toolInvocations: types.maybe(types.frozen(JsonType)),
    annotations: types.maybe(types.frozen(JsonType)),
    internalUpdateId: types.maybe(types.string),
    feedback: types.maybe(types.enumeration(['up', 'down'])),
    carouselItems: types.array(ItemReference),
}).actions((self) => ({
    setContent(content) {
        self.content = content;
    },
    setCreatedAt(createdAt) {
        self.createdAt = createdAt;
    },
    setCarouselItems(items) {
        // console.log('Setting carousel items', items);
        const itemReferences = Object.values(items);
        // eslint-disable-next-line no-restricted-syntax
        for (const itemReference of itemReferences) {
            if (!self.carouselItems.find((i) => (itemReference.key === i.key))) {
                // console.log('added carousel item', itemReference);
                self.carouselItems.push(itemReference);
            }
        }
    },
    setToolInvocations(toolInvocations) {
        self.toolInvocations = toolInvocations;
    },
    setFeedback(feedback) {
        if (self.feedback !== feedback) {
            self.feedback = feedback;
        }
    },
}));
/**
 * If the message contents a partially output <Refinement /> tag, don't append it yet
 * @param content
 */
const cleanContent = (content) => {
    const openTagRegex = /<([a-zA-Z]+)(\s[^<>]*?)?>/gi;
    let lastOpen = null;
    let match = openTagRegex.exec(content);
    while (match !== null) {
        lastOpen = {
            tag: match[1].toLowerCase(),
            index: match.index ?? 0,
        };
        match = openTagRegex.exec(content);
    }
    if (lastOpen) {
        const { tag, index } = lastOpen;
        const closeTagRegex = new RegExp(`<\\/${tag}>`, 'i');
        const selfCloseRegex = new RegExp(`<${tag}[^<>]*?\\/\\s*>`, 'i');
        const sliced = content.slice(index);
        const hasClosing = closeTagRegex.test(sliced);
        const isSelfClosing = selfCloseRegex.test(sliced);
        if (!hasClosing && !isSelfClosing) {
            return `${content.slice(0, index)}...`;
        }
    }
    const incompleteMatch = content.match(/<([a-zA-Z]+)(\s[^<>]*)?$/i);
    if (incompleteMatch) {
        const index = content.lastIndexOf(incompleteMatch[0]);
        return `${content.slice(0, index)}...`;
    }
    return content;
};
// const PageHistory = types.array(types.model({
//   url: types.string,
//   title: types.string,
//   description: types.string,
// }));
const CustomerContext = types.model({
    currentPage: types.maybe(types.model({
        url: types.string,
        title: types.string,
        description: types.maybe(types.string),
        isProduct: types.optional(types.boolean, false),
        isCollection: types.optional(types.boolean, false),
        locale: types.maybe(types.string),
        currency: types.maybe(types.string),
        product: types.maybe(types.model({
            id: types.string,
            name: types.string,
            handle: types.maybe(types.string),
            selectedVariantId: types.maybe(types.string),
            // description: types.maybe(types.string),
            // image: types.maybe(types.string),
            // url: types.maybe(types.string),
        })),
        collection: types.maybe(types.model({
            id: types.string,
            name: types.string,
            handle: types.maybe(types.string),
        })),
    })),
    locale: types.maybe(types.string),
    currency: types.optional(types.string, 'USD'),
    isLoggedIn: types.optional(types.boolean, false),
    geoLocation: types.maybe(types.model({
        lat: types.number,
        lng: types.number,
    })),
    // pageHistory: types.array(PageHistory),
}).actions((self) => ({
    setCurrentPage(data) {
        // console.log('setting current page', data);
        self.currentPage = data.currentPage;
        // Update locale from currentPage if available
        if (data.currentPage?.locale) {
            self.locale = data.currentPage.locale;
        }
        // Also update locale if provided at top level
        if (data.locale) {
            self.locale = data.locale;
        }
        // Update currency from currentPage if available
        if (data.currentPage?.currency) {
            self.currency = data.currentPage.currency;
        }
        // Also update currency if provided at top level
        if (data.currency) {
            self.currency = data.currency;
        }
    },
    setLoggedIn(isLoggedIn) {
        self.isLoggedIn = isLoggedIn;
    },
    setLocale(locale) {
        self.locale = locale;
        if (self.currentPage) {
            self.currentPage.locale = locale;
        }
    },
    setCurrency(currency) {
        self.currency = currency;
        if (self.currentPage) {
            self.currentPage.currency = currency;
        }
    },
    setGeoLocation(geo) {
        self.geoLocation = geo
            ? { lat: geo.lat, lng: geo.lng }
            : undefined;
    },
}));
const ConversationModel = types.model({
    id: types.identifier,
    createdAt: types.Date,
    updatedAt: types.Date,
    messages: types.optional(types.array(ConversationMessage), []),
    itemReferences: types.map(ItemReference),
    systemPrompt: types.maybe(types.string),
    modelID: types.maybe(types.string),
    carouselItems: types.array(types.union(
    // types.reference(types.union(Course, Document, Microcast, Program)),
    // this is so it can hold products, even thoug not supported yet
    types.frozen(JsonType))),
}).views((self) => ({
    get lastMessage() {
        return self.messages[self.messages.length - 1];
    },
    hasMessages() {
        return self.messages.length > 0;
    },
})).actions((self) => {
    let append;
    return {
        connect(newAppend) {
            append = newAppend;
        },
        removeMessageById(id) {
            const index = self.messages.findIndex((m) => m.id === id);
            if (index === -1) {
                return false;
            }
            self.messages.splice(index, 1);
            self.updatedAt = new Date();
            return true;
        },
        removeLastUserMessage() {
            const lastMessage = self.messages[self.messages.length - 1];
            if (!lastMessage || lastMessage.role !== 'user') {
                return false;
            }
            self.messages.pop();
            self.updatedAt = new Date();
            return true;
        },
        /**
         * Align MobX `messages` with the full `useChat` array. Syncing only the last message
         * misses in-place updates to earlier assistant turns (multi-step tools) and can leave
         * the UI stuck on the first assistant reply.
         */
        syncMessagesFromChat(incoming) {
            const filtered = incoming.filter((m) => m.role !== 'function');
            while (self.messages.length > filtered.length) {
                self.messages.pop();
            }
            const applyToolFeedbackCarousel = (target, msg) => {
                if (msg.toolInvocations) {
                    target.setToolInvocations(msg.toolInvocations);
                }
                if (msg.feedback) {
                    target.setFeedback(msg.feedback);
                }
                if (target.carouselItems.length === 0
                    && msg.annotations?.[0]?.itemReferences) {
                    target.setCarouselItems(msg.annotations[0].itemReferences);
                }
            };
            const cleanedFromChat = (msg) => cleanContent(getChatMessageTextForSync(msg));
            for (let i = 0; i < filtered.length; i += 1) {
                const msg = filtered[i];
                const ts = new Date(msg.createdAt ?? msg.timestamp ?? Date.now());
                if (i >= self.messages.length) {
                    self.messages.push({
                        id: msg.id ?? uuidv4(),
                        content: cleanedFromChat(msg),
                        role: msg.role,
                        createdAt: ts,
                    });
                    applyToolFeedbackCarousel(self.messages[self.messages.length - 1], msg);
                    continue;
                }
                const existing = self.messages[i];
                const sameId = existing.id === msg.id || (existing.id == null && msg.id == null);
                if (sameId && existing.role === msg.role) {
                    existing.setContent(cleanedFromChat(msg));
                    existing.setCreatedAt(ts);
                    applyToolFeedbackCarousel(existing, msg);
                }
                else {
                    while (self.messages.length > i) {
                        self.messages.pop();
                    }
                    for (let j = i; j < filtered.length; j += 1) {
                        const m = filtered[j];
                        self.messages.push({
                            id: m.id ?? uuidv4(),
                            content: cleanedFromChat(m),
                            role: m.role,
                            createdAt: new Date(m.createdAt ?? m.timestamp ?? Date.now()),
                        });
                        applyToolFeedbackCarousel(self.messages[self.messages.length - 1], m);
                    }
                    break;
                }
            }
            self.updatedAt = new Date();
        },
        appendMessage(message) {
            // feedback messages are not to be appended
            if (message.role === 'function') {
                if (message.feedback && message.messageId) {
                    const msgToUpdate = self.messages.find((m) => m.id === message.messageId);
                    if (msgToUpdate) {
                        msgToUpdate.setFeedback(message.feedback);
                        self.updatedAt = new Date();
                    }
                    else {
                        console.warn('Could not find message to apply feedback to', message.messageId);
                    }
                }
                return;
            }
            let lastMessage = self.messages[self.messages.length - 1];
            // Same logical message (AI SDK message id): streaming and addToolResult updates merge here.
            // Different id with same role = another assistant turn (multi-step tools) — must push, not merge.
            const sameLogicalMessage = lastMessage?.id === message.id
                || (lastMessage?.id == null && message.id == null);
            const incomingCleaned = cleanContent(getChatMessageTextForSync(message));
            // Same content + same tool *count* is not enough: addToolResult fills results in place
            // without changing length — must still run setToolInvocations below.
            if (sameLogicalMessage
                && lastMessage?.content === incomingCleaned
                && lastMessage?.toolInvocations?.length === message?.toolInvocations?.length) {
                const prevJson = JSON.stringify(lastMessage?.toolInvocations ?? []);
                const nextJson = JSON.stringify(message?.toolInvocations ?? []);
                if (prevJson === nextJson) {
                    return;
                }
            }
            if (lastMessage?.role === message.role && sameLogicalMessage) {
                lastMessage.setContent(incomingCleaned);
                lastMessage.setCreatedAt(new Date(message.createdAt ?? message.timestamp ?? Date.now()));
            }
            else {
                self.messages.push({
                    id: message.id,
                    content: incomingCleaned,
                    role: message.role,
                    createdAt: new Date(message.createdAt ?? message.timestamp ?? Date.now()),
                });
                lastMessage = self.messages[self.messages.length - 1];
            }
            if (message.toolInvocations) {
                lastMessage.setToolInvocations(message.toolInvocations);
            }
            if (message.feedback) {
                lastMessage.setFeedback(message.feedback);
            }
            // console.log('appending message', {
            //   carouselItems: lastMessage.carouselItems.length,
            //   annotations: message.annotations,
            // });
            if (lastMessage.carouselItems.length === 0
                // this is a hacky way to avoid setting carousel items on tool invocation mesage,
                // but instead the following message where results are presented
                // - ai-sdk 4.x works a bit differently and this may not be necessary
                // && !message.toolInvocations
                && message.annotations
                && message.annotations[0]?.itemReferences) {
                // console.log('setting carousel items on message', message.id);
                lastMessage.setCarouselItems(message.annotations[0].itemReferences);
            }
            self.updatedAt = new Date();
        },
        addReference(key, reference) {
            // console.log('Adding reference', { key, reference });
            if (self.itemReferences.has(key)) {
                const existingReference = self.itemReferences.get(key);
                if (!existingReference) {
                    return;
                }
                let didUpdate = false;
                const mergeIfDefined = (field) => {
                    const incomingValue = reference?.[field];
                    if (incomingValue === undefined) {
                        return;
                    }
                    if (existingReference[field] !== incomingValue) {
                        existingReference[field] = incomingValue;
                        didUpdate = true;
                    }
                };
                // Keep references fresh across repeated searches/conversations
                // so flags like customizable are not stuck with stale values.
                mergeIfDefined('type');
                mergeIfDefined('title');
                mergeIfDefined('image');
                mergeIfDefined('description');
                mergeIfDefined('url');
                mergeIfDefined('id');
                mergeIfDefined('organizationID');
                mergeIfDefined('itemID');
                mergeIfDefined('slug');
                mergeIfDefined('price');
                mergeIfDefined('variants');
                mergeIfDefined('showAddToCart');
                mergeIfDefined('customizable');
                const carouselIdx = self.carouselItems.findIndex((c) => c && typeof c === 'object' && c.key === key);
                if (carouselIdx >= 0) {
                    // carouselItems holds frozen snapshots; keep them in sync when streaming merges
                    // fields (e.g. image URL) into the live map entry.
                    self.carouselItems[carouselIdx] = getSnapshot(existingReference);
                }
                if (didUpdate) {
                    self.updatedAt = new Date();
                }
                return;
            }
            self.itemReferences.put(reference);
            const storedReference = self.itemReferences.get(key);
            if (['course', 'document', 'microcast', 'program', 'web', 'product'].includes(reference.type)) {
                if (storedReference && !self.carouselItems.some((c) => c?.key === reference.key)) {
                    self.carouselItems.push(getSnapshot(storedReference));
                }
            }
            // This wont work because VeriticalMediaCard doesnt support products
            // if (item.type === 'product' && !self.carouselItems.includes(item)) {
            //   self.carouselItems.push(item);
            // }
            self.updatedAt = new Date();
        },
        appendReferences(references) {
            // console.log('appendReferences', references);
            Object.keys(references).forEach((key) => {
                this.addReference(key, references[key]);
            });
        },
        setSystemPrompt(systemPrompt) {
            self.systemPrompt = systemPrompt;
            self.updatedAt = new Date();
        },
        setModelID(modelID) {
            self.modelID = modelID;
            self.updatedAt = new Date();
        },
        sendPrompt(prompt) {
            if (!append) {
                console.warn('[ConversationModel] sendPrompt called but append is not connected');
                return;
            }
            append({ content: prompt, role: 'user' });
        },
        sendTrackingEvent(event, additionalData = {}) {
            const data = {
                event: `conversationAI_${event}`,
                conversationID: self.id,
                ...additionalData,
            };
            // console.log('conversationEvent', data);
            if (typeof window === 'undefined' || !window) {
                return;
            }
            window.top?.postMessage({ source: 'microcommerce-ai-embed', type: 'tracking', trackData: data }, '*');
            if (window.gtag) {
                window.gtag('event', `conversationAI_${event}`, omit(data, 'event'));
            }
        },
    };
});
export { ConversationModel, ConversationMessage, CustomerContext, };
//# sourceMappingURL=ConversationModel.js.map